import React, {useCallback, useEffect, useState} from "react";
import PropTypes from "prop-types";
import TextField from "@material-ui/core/TextField";
import {
    useDeletePartnerContent,
    useGetHomeContent,
    useGetPartnerContent,
    usePatchHomeContent,
    usePatchPartnerContent,
    usePostPartnerContent
} from "../../../../Data/ApiHooks/UseContentHooks";
import {TextareaAutosize} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import {useForm} from "react-hook-form";
import z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import useLocalizedArray from "../../../../CustomHooks/useLocalizedArray";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import PlaceholderImage from "../../../../Assets/placeholder-image.png";
import useSnackbar from "../../../../CustomHooks/useSnackbar";
import {IMAGE_BASE_URL} from "../../../../Constants/config";

const HomeContentTab = ({ setShowEditedAlert }) => {
    // region Constants
    const formSchema = z.object({
        bookDriverPhoneNumber: z.string().min(1, "This field is required"),
        sloganTitle: z.string().min(1, "This field is required"),
        sloganDescription: z.string().min(1, "This field is required"),
        aboutUsTitle: z.string().min(1, "This field is required"),
        aboutUsDescription: z.string().min(1, "This field is required"),
        hero_image: z.any(),
        section_image: z.any(),
        vehicleServiceTitle: z.string().min(1, "This field is required"),
        vehicleServiceDescription: z.string().min(1, "This field is required"),
        driverServiceTitle: z.string().min(1, "This field is required"),
        driverServiceDescription: z.string().min(1, "This field is required"),
        operator_1_name: z.string().min(1, "This field is required"),
        operator_1_function: z.string().min(1, "This field is required"),
        operator_1_phone: z.string().min(1, "This field is required"),
        operator_2_name: z.string().min(1, "This field is required"),
        operator_2_function: z.string().min(1, "This field is required"),
        operator_2_phone: z.string().min(1, "This field is required"),
        contact_title: z.string().min(1, "This field is required"),
        contact_phone: z.string().min(1, "This field is required"),
        contact_email: z.string().email("Please enter a valid email address"),
        monday_friday_AM: z.string().optional(),
        monday_friday_PM: z.string().optional(),
        saturday_AM: z.string().optional(),
        saturday_PM: z.string().optional(),
        sunday_AM: z.string().optional(),
        sunday_PM: z.string().optional()
    });

    const localizedText = useLocalizedArray([
        "book-driver-phone-number-title",
        "slogan-title",
        "slogan-title-description",
        "hero-image",
        "about-us-title",
        "about-us-description",
        "section-image",
        "vehicle-service-title",
        "vehicle-service-description",
        "driver-service-title",
        "driver-service-description",
        "partners-title",
        "browse-image",
        "operator1Name",
        "operator1Function",
        "operator1Phone",
        "operator2Name",
        "operator2Function",
        "operator2Phone",
        "contactTitle",
        "contactPhone",
        "contactEmail",
        "business-hours"
    ]);
    // endregion

    const [heroImage, setHeroImage] = useState(null);
    const [sectionImage, setSectionImage] = useState(null);
    const [partnerImages, setPartnerImages] = useState([]);

    // region Queries
    const { data, refetch: refetchHomeData } = useGetHomeContent();
    const { data: partnerData, refetch: refetchPartnerData } = useGetPartnerContent();
    const { mutate, isLoading } = usePatchHomeContent({ onSuccess: () => onPatchHomeContentSuccess(), onError: (error) => onPatchHomeContentError(error) });
    const { mutate: patchPartnerContent } = usePatchPartnerContent({ onSuccess: () => refetchPartnerData(), onError: (error) => onPatchHomeContentError(error) });
    const { mutate: postPartnerContent } = usePostPartnerContent({ onSuccess: () => refetchPartnerData(), onError: (error) => onPatchHomeContentError(error) });
    const { mutate: deletePartnerContent } = useDeletePartnerContent({ onSuccess: () => refetchPartnerData(), onError: (error) => onPatchHomeContentError(error) });
    // endregion

    // region Custom hooks
    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
        reset
    } = useForm({
        resolver: zodResolver(formSchema),
    });

    const { showSnackBar } = useSnackbar();
    // endregion

    // region Query callback functions

    function onPatchHomeContentSuccess() {
        showSnackBar("Home content updated successfully", "success");
        refetchHomeData();
    }

    function onPatchHomeContentError(error) {
        showSnackBar(error, "error");
    }

    // endregion

    // region Functions

    const createImageUrl = useCallback((imageUrl) => {
        if (!imageUrl) {
            return PlaceholderImage;
        }

        return imageUrl.includes("blob") ? imageUrl : IMAGE_BASE_URL + imageUrl;
    }, [])

    const savePartnerImages = useCallback(() => {
        if (!partnerImages) {
            return;
        }

        const newImages = [...partnerImages];

        for (let index = 0; index < partnerImages.length; index++) {
            const formData = new FormData();
            const partner = partnerImages[index];
            const isNewImage = partner.id.includes("new-");

            formData.append("tags", partner.tags);

            if (partner.imageFile) {
                formData.append("image", partner.imageFile);
            }

            if (isNewImage) {
                postPartnerContent(formData);
            } else {
                formData.append("id", partner.id);
                patchPartnerContent(formData);
            }
        }

        setPartnerImages(newImages);
    }, [partnerImages, patchPartnerContent, postPartnerContent]);

    // endregion

    // region Handler functions
    const handleOnSubmit = useCallback((formData) => {
        if (!data) {
            return;
        }

        const formPayload = new FormData();
        formPayload.append("id", data.id);
        formPayload.append("book_driver_phone", formData.bookDriverPhoneNumber || "");
        formPayload.append("slogan_title", formData.sloganTitle || "");
        formPayload.append("slogan_description", formData.sloganDescription || "");
        formPayload.append("about_title", formData.aboutUsTitle || "");
        formPayload.append("about_description", formData.aboutUsDescription || "");
        formPayload.append("vehicle_service_title", formData.vehicleServiceTitle || "");
        formPayload.append("vehicle_service_description", formData.vehicleServiceDescription || "");
        formPayload.append("driver_service_title", formData.driverServiceTitle || "");
        formPayload.append("driver_service_description", formData.driverServiceDescription || "");
        formPayload.append("operator_1_name", formData.operator_1_name || "");
        formPayload.append("operator_1_function", formData.operator_1_function || "");
        formPayload.append("operator_1_phone", formData.operator_1_phone || "");
        formPayload.append("operator_2_name", formData.operator_2_name || "");
        formPayload.append("operator_2_function", formData.operator_2_function || "");
        formPayload.append("operator_2_phone", formData.operator_2_phone || "");
        formPayload.append("contact_title", formData.contact_title || "");
        formPayload.append("contact_phone", formData.contact_phone || "");
        formPayload.append("contact_email", formData.contact_email || "");
        formPayload.append("business_hours", JSON.stringify({
            "mon-fri": [formData.monday_friday_AM || "08:00", formData.monday_friday_PM || "18:00"],
            "sat": [formData.saturday_AM || "09:00", formData.saturday_PM || "14:00"],
            "sun": []
        }));

        if (heroImage && heroImage.image.includes("blob")) {
            formPayload.append("hero_image", heroImage.imageFile);
        }

        if (sectionImage && sectionImage.image.includes("blob")) {
            formPayload.append("section_image", sectionImage.imageFile);
        }

        mutate(formPayload);
        savePartnerImages();
    }, [data, mutate, heroImage, sectionImage, savePartnerImages]);

    const handleImageDelete = useCallback((id) => {
        deletePartnerContent(id);
    }, [deletePartnerContent]);

    const handleNewPartnerCreation = useCallback(() => {
        setPartnerImages((prevState) => [...prevState, { id: `new-${Date.now()}`, image: PlaceholderImage, tags: "" }]);
    }, []);

    const handlePartnerImage = useCallback((event, index) => {
        if (!partnerImages || !partnerImages[index]) {
            return;
        }

        const newImages = [...partnerImages];

        if (event.target.files && event.target.files[0]) {
            newImages[index] = {
                ...newImages[index],
                image: URL.createObjectURL(event.target.files[0]),
                imageFile: event.target.files[0]
            };
        }

        setPartnerImages(newImages);
    }, [partnerImages]);

    // endregion

    // region UI Render functions
    const renderBookDriverSection = useCallback(() => (
        <div className="home-editor-form-input-container">
            <span className="field-title">{localizedText.bookDriverPhoneNumberTitle}</span>
            <TextField
                {...register("bookDriverPhoneNumber")}
                className="home-editor-form-input"
                helperText={errors.bookDriverPhoneNumber?.message}
                error={!!errors.bookDriverPhoneNumber}
                fullWidth
                placeholder="Enter phone number"
            />
        </div>
    ), [localizedText.bookDriverPhoneNumberTitle, register, errors.bookDriverPhoneNumber]);

    const renderSloganSection = useCallback(() => (
        <div className="form-section-container">
            <div className="form-section">
                <div className="home-editor-form-input-container">
                    <span className="field-title">{localizedText.sloganTitle}</span>
                    <TextField
                        {...register("sloganTitle")}
                        className="home-editor-form-input"
                        helperText={errors.sloganTitle?.message}
                        error={!!errors.sloganTitle}
                        fullWidth
                        placeholder="Enter slogan title"
                    />
                </div>
                <div className="home-editor-form-input-container">
                    <span className="field-title">{localizedText.sloganTitleDescription}</span>
                    <TextareaAutosize
                        {...register("sloganDescription")}
                        className="home-editor-form-input-textarea"
                        style={{ width: "100%" }}
                        placeholder="Enter slogan description"
                    />
                    {errors.sloganDescription?.message && (
                        <p className="error-message">{errors.sloganDescription?.message}</p>
                    )}
                </div>
            </div>
            <div className="home-editor-form-input-container">
                <span className="field-title">{localizedText.heroImage}</span>
                {data && (
                    <img
                        src={createImageUrl(heroImage?.image)}
                        alt="Current hero"
                        style={{ height: "250px", width: "350px", marginBottom: "10px", objectFit: "contain" }}
                    />
                )}
                <input
                    type="file"
                    accept="image/*"
                    className="home-editor-form-file-input"
                    id="section-hero-image-upload"
                    onChange={(e) => {
                        setHeroImage({
                            image: URL.createObjectURL(e.target.files[0]),
                            imageFile: e.target.files[0]
                        });
                    }}
                />
                <label htmlFor="section-hero-image-upload">
                    <Button
                        className="new-image-upload-button"
                        component="span"
                    >
                        {localizedText.browseImage}
                    </Button>
                </label>
            </div>
        </div>
    ), [localizedText.sloganTitle, localizedText.sloganTitleDescription, localizedText.heroImage, localizedText.browseImage, register, errors.sloganTitle, errors.sloganDescription?.message, data, heroImage]);

    const renderAboutUsSection = useCallback(() => (
        <div className="form-section-container">
            <div className="form-section">
                <div className="home-editor-form-input-container">
                    <span className="field-title">{localizedText.aboutUsTitle}</span>
                    <TextField
                        {...register("aboutUsTitle")}
                        className="home-editor-form-input"
                        helperText={errors.aboutUsTitle?.message}
                        error={!!errors.aboutUsTitle}
                        fullWidth
                        placeholder="Enter about us title"
                    />
                </div>

                <div className="home-editor-form-input-container">
                    <span className="field-title">{localizedText.aboutUsDescription}</span>
                    <TextareaAutosize
                        {...register("aboutUsDescription")}
                        className="home-editor-form-input-textarea"
                        helperText={errors.aboutUsDescription?.message}
                        error={!!errors.aboutUsDescription}
                        fullWidth
                        placeholder="Enter about us description"
                    />
                </div>
            </div>

            <div className="home-editor-form-input-container">
                <span className="field-title">{localizedText.sectionImage}</span>
                {data && (
                    <img
                        src={createImageUrl(sectionImage?.image)}
                        alt="Current section"
                        style={{ height: "250px", width: "350px", marginBottom: "10px", objectFit: "contain" }}
                    />
                )}
                <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    id="section-image-upload"
                    onChange={(e) => {
                        setSectionImage({
                            image: URL.createObjectURL(e.target.files[0]),
                            imageFile: e.target.files[0]
                        });
                    }}
                />
                <label htmlFor="section-image-upload">
                    <Button
                        className="new-image-upload-button"
                        component="span"
                    >
                        {localizedText.browseImage}
                    </Button>
                </label>
            </div>
        </div>
    ), [localizedText.aboutUsTitle, localizedText.aboutUsDescription, localizedText.sectionImage, localizedText.browseImage, register, errors.aboutUsTitle, errors.aboutUsDescription, data, sectionImage]);

    const renderServicesSection = useCallback(() => (
        <div className="form-section">
            <div className="home-editor-form-input-container">
                <span className="field-title">{localizedText.vehicleServiceTitle}</span>
                <TextField
                    {...register("vehicleServiceTitle")}
                    className="home-editor-form-input"
                    helperText={errors.vehicleServiceTitle?.message}
                    error={!!errors.vehicleServiceTitle}
                    fullWidth
                    placeholder="Enter vehicle service title"
                />
            </div>

            <div className="home-editor-form-input-container">
                <span className="field-title">{localizedText.vehicleServiceDescription}</span>
                <TextareaAutosize
                    {...register("vehicleServiceDescription")}
                    className="home-editor-form-input-textarea"
                    helperText={errors.vehicleServiceDescription?.message}
                    error={!!errors.vehicleServiceDescription}
                    fullWidth
                    placeholder="Enter vehicle service description"
                />
            </div>

            <div className="home-editor-form-input-container">
                <span className="field-title">{localizedText.driverServiceTitle}</span>
                <TextField
                    {...register("driverServiceTitle")}
                    className="home-editor-form-input"
                    helperText={errors.driverServiceTitle?.message}
                    error={!!errors.driverServiceTitle}
                    fullWidth
                    placeholder="Enter driver service title"
                />
            </div>

            <div className="home-editor-form-input-container">
                <span className="field-title">{localizedText.driverServiceDescription}</span>
                <TextareaAutosize
                    {...register("driverServiceDescription")}
                    className="home-editor-form-input-textarea"
                    helperText={errors.driverServiceDescription?.message}
                    error={!!errors.driverServiceDescription}
                    fullWidth
                    placeholder="Enter driver service description"
                />
            </div>
        </div>
    ), [errors.driverServiceDescription, errors.driverServiceTitle, errors.vehicleServiceDescription, errors.vehicleServiceTitle, register, localizedText]);

    const renderPartnersSection = useCallback(() => (
        <div className="home-editor-form-input-container-grid">
            <span className="field-title">{localizedText.partnersTitle}</span>
            <div className="home-editor-form-grid">
                <Grid container spacing={2}>
                    {partnerImages?.map((image, index) => (
                        <Grid item xs={4} key={image.id}>
                            <div className="partner-image-container">
                                <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    id={`partner-image-upload-${image.id}`}
                                    onChange={(e) => handlePartnerImage(e, index)}
                                />
                                <label htmlFor={`partner-image-upload-${image.id}`}>
                                    <img
                                        src={createImageUrl(image.image)}
                                        alt={`Gallery image ${index + 1}`}
                                        style={{ width: "100%", height: "200px", objectFit: "cover", cursor: "pointer" }}
                                    />
                                </label>
                                <TextField
                                    {...register(`partnerLinks.${image.id}`)}
                                    fullWidth
                                    placeholder="Enter partner link"
                                    className="home-editor-form-input"
                                    style={{ marginTop: "10px" }}
                                    value={image.tags}
                                    onChange={(e) => {
                                        const newTags = [...partnerImages];
                                        newTags[index].tags = e.target.value;
                                        setPartnerImages(newTags);
                                    }}
                                />
                                <Button
                                    className="partner-delete-button"
                                    onClick={() => handleImageDelete(image.id)}
                                >
                                    <DeleteForeverIcon/>
                                </Button>
                            </div>
                        </Grid>
                    ))}
                    <Grid item xs={4}>
                        <div className="home-editor-form-uploaded-grid">
                            <label htmlFor="new-partner-image-upload">
                                <Button onClick={() => handleNewPartnerCreation()} className="new-image-upload-button">
                                    Add partner logo
                                </Button>
                            </label>
                        </div>
                    </Grid>
                </Grid>
            </div>
        </div>
    ), [localizedText.partnersTitle, partnerImages, register, handlePartnerImage, handleImageDelete, handleNewPartnerCreation]);

    const renderDetailSection = useCallback(() => {
        return (
            <div className="form-section">
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <div className="home-editor-form-input-container">
                            <span className="field-title">{localizedText.operator1Name}</span>
                            <TextField
                                {...register("operator_1_name")}
                                className="home-editor-form-input"
                                helperText={errors.operator_1_name?.message}
                                error={!!errors.operator_1_name}
                                fullWidth
                                placeholder="Enter operator 1 name"
                            />
                        </div>
                    </Grid>
                    <Grid item xs={4}>
                        <div className="home-editor-form-input-container">
                            <span className="field-title">{localizedText.operator1Function}</span>
                            <TextField
                                {...register("operator_1_function")}
                                className="home-editor-form-input"
                                helperText={errors.operator_1_function?.message}
                                error={!!errors.operator_1_function}
                                fullWidth
                                placeholder="Enter operator 1 function"
                            />
                        </div>
                    </Grid>
                    <Grid item xs={4}>
                        <div className="home-editor-form-input-container">
                            <span className="field-title">{localizedText.operator1Phone}</span>
                            <TextField
                                {...register("operator_1_phone")}
                                className="home-editor-form-input"
                                helperText={errors.operator_1_phone?.message}
                                error={!!errors.operator_1_phone}
                                fullWidth
                                placeholder="Enter operator 1 phone"
                            />
                        </div>
                    </Grid>
                    <Grid item xs={4}>
                        <div className="home-editor-form-input-container">
                            <span className="field-title">{localizedText.operator2Name}</span>
                            <TextField
                                {...register("operator_2_name")}
                                className="home-editor-form-input"
                                helperText={errors.operator_2_name?.message}
                                error={!!errors.operator_2_name}
                                fullWidth
                                placeholder="Enter operator 2 name"
                            />
                        </div>
                    </Grid>
                    <Grid item xs={4}>
                        <div className="home-editor-form-input-container">
                            <span className="field-title">{localizedText.operator2Function}</span>
                            <TextField
                                {...register("operator_2_function")}
                                className="home-editor-form-input"
                                helperText={errors.operator_2_function?.message}
                                error={!!errors.operator_2_function}
                                fullWidth
                                placeholder="Enter operator 2 function"
                            />
                        </div>
                    </Grid>
                    <Grid item xs={4}>
                        <div className="home-editor-form-input-container">
                            <span className="field-title">{localizedText.operator2Phone}</span>
                            <TextField
                                {...register("operator_2_phone")}
                                className="home-editor-form-input"
                                helperText={errors.operator_2_phone?.message}
                                error={!!errors.operator_2_phone}
                                fullWidth
                                placeholder="Enter operator 2 phone"
                            />
                        </div>
                    </Grid>
                    <Grid item xs={8}>
                        <div className="home-editor-form-input-container">
                            <span className="field-title">{localizedText.contactTitle}</span>
                            <TextField
                                {...register("contact_title")}
                                className="home-editor-form-input"
                                helperText={errors.contact_title?.message}
                                error={!!errors.contact_title}
                                fullWidth
                                placeholder="Enter contact title"
                            />
                        </div>
                    </Grid>
                    <Grid item xs={8}>
                        <div className="home-editor-form-input-container">
                            <span className="field-title">{localizedText.contactPhone}</span>
                            <TextField
                                {...register("contact_phone")}
                                className="home-editor-form-input"
                                helperText={errors.contact_phone?.message}
                                error={!!errors.contact_phone}
                                fullWidth
                                placeholder="Enter contact phone"
                            />
                        </div>
                    </Grid>
                    <Grid item xs={8}>
                        <div className="home-editor-form-input-container">
                            <span className="field-title">{localizedText.contactEmail}</span>
                            <TextField
                                {...register("contact_email")}
                                className="home-editor-form-input"
                                helperText={errors.contact_email?.message}
                                error={!!errors.contact_email}
                                fullWidth
                                placeholder="Enter contact email"
                            />
                        </div>
                    </Grid>
                </Grid>
            </div>
        );
    }, [errors.operator_1_name, errors.operator_1_function, errors.operator_1_phone, errors.operator_2_name, errors.operator_2_function, errors.operator_2_phone, errors.contact_title, errors.contact_phone, errors.contact_email, register, localizedText]);

    const renderHourSection = useCallback(() => {
        return (
            <div className="form-section-hours">
                <span className="field-title">{localizedText.businessHours}</span>
                <Grid container spacing={1}>
                    <Grid item xs={4}>
                        <div className="white-box">
                            Mon-Friday
                        </div>
                    </Grid>
                    <Grid item xs={1}>
                        <div className="white-box">
                            AM
                        </div>
                    </Grid>
                    <Grid item xs={3}>
                        <div className="home-editor-form-input-container">
                            <TextField
                                {...register("monday_friday_AM")}
                                className="home-editor-form-time-input"
                                helperText={errors.monday_friday_AM?.message}
                                error={!!errors.monday_friday_AM}
                                fullWidth
                                type="time"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    step: 300
                                }}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={1}>
                        <div className="white-box">
                            PM
                        </div>
                    </Grid>
                    <Grid item xs={3}>
                        <div className="home-editor-form-input-container">
                            <TextField
                                {...register("monday_friday_PM")}
                                className="home-editor-form-time-input"
                                helperText={errors.monday_friday_PM?.message}
                                error={!!errors.monday_friday_PM}
                                fullWidth
                                type="time"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    step: 300
                                }}
                                defaultValue={data?.business_hours?.["mon-fri"]?.[1] || "18:00"}
                                // Remove defaultValue prop
                            />
                        </div>
                    </Grid>
                    {/* Apply the same pattern to all other TextField components in this section */}
                    <Grid item xs={4}>
                        <div className="white-box">
                            Saturday
                        </div>
                    </Grid>
                    <Grid item xs={1}>
                        <div className="white-box">
                            AM
                        </div>
                    </Grid>
                    <Grid item xs={3}>
                        <div className="home-editor-form-input-container">
                            <TextField
                                {...register("saturday_AM")}
                                className="home-editor-form-time-input"
                                helperText={errors.saturday_AM?.message}
                                error={!!errors.saturday_AM}
                                fullWidth
                                type="time"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    step: 300
                                }}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={1}>
                        <div className="white-box">
                            PM
                        </div>
                    </Grid>
                    <Grid item xs={3}>
                        <div className="home-editor-form-input-container">
                            <TextField
                                {...register("saturday_PM")}
                                className="home-editor-form-time-input"
                                helperText={errors.saturday_PM?.message}
                                error={!!errors.saturday_PM}
                                fullWidth
                                type="time"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    step: 300
                                }}
                                defaultValue={data?.business_hours?.sat?.[1] || "14:00"}
                                // Remove defaultValue prop
                            />
                        </div>
                    </Grid>
                    <Grid item xs={4}>
                        <div className="white-box">
                            Sunday
                        </div>
                    </Grid>
                    <Grid item xs={1}>
                        <div className="white-box">
                            AM
                        </div>
                    </Grid>
                    <Grid item xs={3}>
                        <div className="home-editor-form-input-container">
                            <TextField
                                {...register("sunday_AM")}
                                className="home-editor-form-time-input"
                                helperText={errors.sunday_AM?.message}
                                error={!!errors.sunday_AM}
                                fullWidth
                                type="time"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    step: 300
                                }}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={1}>
                        <div className="white-box">
                            PM
                        </div>
                    </Grid>
                    <Grid item xs={3}>
                        <div className="home-editor-form-input-container">
                            <TextField
                                {...register("sunday_PM")}
                                className="home-editor-form-time-input"
                                helperText={errors.sunday_PM?.message}
                                error={!!errors.sunday_PM}
                                fullWidth
                                type="time"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    step: 300
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
            </div>
        );
    }, [data?.business_hours, errors.monday_friday_AM, errors.monday_friday_PM, errors.saturday_AM, errors.saturday_PM, errors.sunday_AM, errors.sunday_PM, localizedText.businessHours, register]);
    // endregion

    // region UseEffects
    useEffect(() => {
        if (!partnerData || !data ) {
            return;
        }

        const businessHours = data?.business_hours;

        reset({
            bookDriverPhoneNumber: data.book_driver_phone || "",
            sloganTitle: data.slogan_title || "",
            sloganDescription: data.slogan_description || "",
            aboutUsTitle: data.about_title || "",
            aboutUsDescription: data.about_description || "",
            hero_image: null, // Don't set file inputs with existing data
            section_image: null, // Don't set file inputs with existing data
            vehicleServiceTitle: data.vehicle_service_title || "",
            vehicleServiceDescription: data.vehicle_service_description || "",
            driverServiceTitle: data.driver_service_title || "",
            driverServiceDescription: data.driver_service_description || "",
            operator_1_name: data.operator_1_name || "",
            operator_1_function: data.operator_1_function || "",
            operator_1_phone: data.operator_1_phone || "",
            operator_2_name: data.operator_2_name || "",
            operator_2_function: data.operator_2_function || "",
            operator_2_phone: data.operator_2_phone || "",
            contact_title: data.contact_title || "",
            contact_phone: data.contact_phone || "",
            contact_email: data.contact_email || "",
            monday_friday_AM: businessHours["mon-fri"][0] || "",
            monday_friday_PM: businessHours["mon-fri"][1] || "",
            saturday_AM: businessHours.sat[0] || "",
            saturday_PM: businessHours.sat[1] || "",
            sunday_AM: businessHours.sun[0] || "",
            sunday_PM: businessHours.sun[1] || ""
        });

        setSectionImage({ image: data.section_image });
        setHeroImage({ image: data.hero_image });
        setPartnerImages(partnerData);
    }, [data, reset, partnerData]);

    useEffect(() => {
        if (isDirty) {
            setShowEditedAlert(isDirty);
        }
    }, [isDirty, setShowEditedAlert]);
    // endregion

    return (
        <form className="home-editor-form" onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(handleOnSubmit)(e);
        }}>
            <div className="button-container">
                <Button className="submit-button" type="submit" disabled={isLoading}>Update</Button>
            </div>
            {renderBookDriverSection()}
            <div className="separator"/>
            {renderSloganSection()}
            <div className="separator"/>
            {renderAboutUsSection()}
            <div className="separator"/>
            {renderServicesSection()}
            <div className="separator"/>
            {renderPartnersSection()}
            <div className="separator"/>
            {renderDetailSection()}
            <div className="separator"/>
            {renderHourSection()}
        </form>
    );
};

HomeContentTab.propTypes = {
    setShowEditedAlert: PropTypes.func
};

export default HomeContentTab;
